import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { useState, useCallback } from 'react';

/**
 * Hook to get sync status
 */
export function useSyncStatus() {
  const status = useQuery(api.sync.getStatus);

  return {
    status,
    isLoading: status === undefined,
    isYnabConnected: status?.ynab.connected ?? false,
    isSyncing: status?.isRunning ?? false,
  };
}

/**
 * Hook to get sync history
 */
export function useSyncHistory(limit = 20) {
  const history = useQuery(api.sync.getHistory, { limit });

  return {
    history: history ?? [],
    isLoading: history === undefined,
  };
}

/**
 * Hook to trigger syncs
 */
export function useTriggerSync() {
  const ynabSync = useAction(api.financeFeed.refresh);
  const eraSync = useAction(api.eraSync.syncBalances);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{
    accountsSynced: number;
    categoriesSynced: number;
    transactionsSynced: number;
  } | null>(null);

  const sync = useCallback(async () => {
    setIsSyncing(true);
    setError(null);

    try {
      const [ynab, era] = await Promise.allSettled([ynabSync(), eraSync({})]);
      if (ynab.status === 'rejected' || era.status === 'rejected') {
        throw new Error([
          ynab.status === 'rejected' ? `YNAB: ${String(ynab.reason)}` : '',
          era.status === 'rejected' ? `Era: ${String(era.reason)}` : '',
        ].filter(Boolean).join('; '));
      }
      const result = ynab.value;
      setLastResult(result);
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Sync failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsSyncing(false);
    }
  }, [ynabSync, eraSync]);

  return {
    sync,
    isSyncing,
    error,
    lastResult,
    clearError: () => setError(null),
  };
}
